// Common peptide "stacks" (combinations used toward a shared goal). Original
// educational writing — not medical advice, not dosing recommendations. Peptide
// `slug`s must match entries in data/peptides.js so cross-links resolve.
export const STACKS = [
  {
    slug: 'healing-recovery',
    name: 'Healing & Recovery Stack',
    tag: 'Recovery',
    icon: '🩹',
    summary: 'BPC-157 + TB-500 — the classic pairing discussed for soft-tissue repair and recovery.',
    peptides: [
      { name: 'BPC-157', slug: 'bpc-157' },
      { name: 'TB-500', slug: 'tb-500' },
    ],
    overview:
      'BPC-157 and TB-500 are the most talked-about recovery pairing. The idea behind combining them is that they’re studied for repair through different mechanisms — so together they’re often discussed as complementary rather than redundant.',
    whyTogether: [
      'BPC-157 is studied mainly for localized soft-tissue and gut protection, often via blood-vessel growth (angiogenesis).',
      'TB-500 (a thymosin beta-4 fragment) is studied for cell migration and systemic recovery.',
      'Because they act through different pathways, they’re commonly framed as a “local + systemic” combination.',
    ],
    cautions: [
      'Both are research compounds, not approved medicines — evidence in humans is limited.',
      'A blend sold as one vial fixes the ratio; separate vials let you handle each independently.',
    ],
  },
  {
    slug: 'growth-hormone',
    name: 'Growth Hormone Stack',
    tag: 'GH support',
    icon: '💪',
    summary: 'CJC-1295 + Ipamorelin — a GHRH paired with a GHRP for a fuller growth-hormone pulse.',
    peptides: [
      { name: 'CJC-1295 (no DAC)', slug: 'cjc-1295-no-dac' },
      { name: 'Ipamorelin', slug: 'ipamorelin' },
    ],
    overview:
      'CJC-1295 and Ipamorelin are frequently paired because they nudge growth-hormone release through two different receptors at once — a GHRH analog plus a selective GHRP — which is why they’re discussed as synergistic.',
    whyTogether: [
      'CJC-1295 (no DAC) mimics growth-hormone-releasing hormone (GHRH).',
      'Ipamorelin is a selective GHRP that triggers a clean GH pulse with minimal effect on cortisol or appetite.',
      'Using both is thought to produce a larger, more natural-shaped pulse than either alone.',
    ],
    cautions: [
      'Timing (e.g. away from meals/before bed) is commonly discussed; follow qualified guidance.',
      'These influence your own hormone release — not a substitute for medical GH therapy.',
    ],
  },
  {
    slug: 'longevity',
    name: 'Longevity & Anti-Aging Stack',
    tag: 'Longevity',
    icon: '⏳',
    summary: 'Epithalon + NAD+ — a telomere/pineal peptide alongside a cellular-energy coenzyme.',
    peptides: [
      { name: 'Epithalon', slug: 'epithalon' },
      { name: 'NAD+', slug: 'nad-plus' },
    ],
    overview:
      'This pairing comes up in longevity circles: Epithalon is studied around the pineal gland and telomerase, while NAD+ is a coenzyme central to cellular energy and repair. The two are discussed as targeting “aging” from different angles.',
    whyTogether: [
      'Epithalon is researched for effects on the pineal gland, melatonin rhythm, and telomerase activity.',
      'NAD+ supports mitochondrial energy production and DNA-repair pathways that decline with age.',
      'Together they’re framed as a rhythm/repair plus cellular-energy approach.',
    ],
    cautions: [
      'Longevity claims are largely preclinical — human evidence is early.',
      'NAD+ infusions/injections can cause flushing or discomfort if given too quickly.',
    ],
  },
  {
    slug: 'cognitive',
    name: 'Cognitive / Nootropic Stack',
    tag: 'Cognition',
    icon: '🧠',
    summary: 'Semax + Selank — a focus-oriented peptide paired with a calming, anti-anxiety one.',
    peptides: [
      { name: 'Semax', slug: 'semax' },
      { name: 'Selank', slug: 'selank' },
    ],
    overview:
      'Semax and Selank are Russian-developed peptides often used together as a “focus + calm” pairing — Semax for alertness and cognition, Selank for anxiety and mood — so the combination is discussed as balanced rather than one-sided.',
    whyTogether: [
      'Semax is studied for focus, memory, and BDNF-related neuroprotection.',
      'Selank is studied for anxiolytic (anti-anxiety) and mood-stabilizing effects.',
      'Pairing a stimulating peptide with a calming one is the rationale behind stacking them.',
    ],
    cautions: [
      'Usually intranasal; delivery and dosing differ from injectable peptides.',
      'Nootropic effects are subjective and evidence is limited.',
    ],
  },
  {
    slug: 'metabolic',
    name: 'Metabolic Support Stack',
    tag: 'Metabolic',
    icon: '🔥',
    summary: 'AOD-9604 + MOTS-c — a fat-metabolism fragment alongside a mitochondrial-derived peptide.',
    peptides: [
      { name: 'AOD-9604', slug: 'aod-9604' },
      { name: 'MOTS-c', slug: 'mots-c' },
    ],
    overview:
      'AOD-9604 and MOTS-c are discussed together for body-composition and metabolic support, since each is studied around fat metabolism and mitochondrial function through distinct mechanisms.',
    whyTogether: [
      'AOD-9604 is a modified fragment of growth hormone studied for fat metabolism without the GH growth effects.',
      'MOTS-c is a mitochondrial-derived peptide studied for metabolic regulation and insulin sensitivity.',
      'The pairing is framed as “fat-mobilization + metabolic efficiency.”',
    ],
    cautions: [
      'Neither replaces the fundamentals of diet, training, and sleep.',
      'Metabolic effects in humans are still being studied.',
    ],
  },
]

export const getStackBySlug = (slug) => STACKS.find((s) => s.slug === slug)
